import React, { useState, useEffect, useMemo } from 'react';
import { ConfigProvider, App, Spin } from 'antd';
import { BrowserRouter as Router, useRoutes } from 'react-router';
import zhCN from 'antd/locale/zh_CN';
import { routers } from "../route";
import { RouterAuth } from "./routerAuth";
import { useCommonStore } from "../store/useCommonStore";

import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

const tokensSmall = {
  borderRadius: 2,
  borderRadiusLG: 4,
  borderRadiusSM: 3,

  controlHeightLG: 32,
  controlHeight: 24,
  controlHeightSM: 20,

  fontSize: 10,
  fontSizeSM: 8,

  margin: 12,
  marginSM: 8,
  marginXS: 4,
  marginXXS: 2,

  paddingXL: 24,
  paddingLG: 16,
  padding: 12,
  paddingSM: 8,
  paddingXS: 4,
  paddingXXS: 2,
};

const tokensMedium = {
  borderRadius: 4,
  borderRadiusLG: 6,
  borderRadiusSM: 3,

  controlHeightLG: 36,
  controlHeight: 28,
  controlHeightSM: 22,

  fontSize: 12,
  fontSizeSM: 10,

  margin: 14,
  marginSM: 10,
  marginXS: 6,
  marginXXS: 3,

  paddingXL: 28,
  paddingLG: 20,
  padding: 14,
  paddingSM: 10,
  paddingXS: 6,
  paddingXXS: 3,
};

const tokensLarge = {
  borderRadius: 6,
  borderRadiusLG: 8,
  borderRadiusSM: 4,

  controlHeightLG: 40,
  controlHeight: 32,
  controlHeightSM: 24,

  fontSize: 14,
  fontSizeSM: 12,

  margin: 16,
  marginSM: 12,
  marginXS: 8,
  marginXXS: 4,

  paddingXL: 32,
  paddingLG: 24,
  padding: 16,
  paddingSM: 12,
  paddingXS: 8,
  paddingXXS: 4,
};

import { generate } from '@ant-design/colors';

const AppRoutes = () => {
	const routes = useRoutes(routers);
	return routes;
};

const Root = (props) => {
  const primaryColor = useCommonStore(state => state.primaryColor);
  const themeMode = useCommonStore(state => state.themeMode);

  // 视口宽度状态
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 动态注入主题 CSS 变量
  useEffect(() => {
    if (primaryColor) {
      const colors = generate(primaryColor);
      const root = document.documentElement;
      root.style.setProperty('--primary-color', primaryColor);
      root.style.setProperty('--primary-color-light', colors[2]); // light hover
      root.style.setProperty('--primary-color-dark', colors[6]);  // active
    }
  }, [primaryColor]);

  // 计算当前使用的主题tokens
  const currentSizeKey = useMemo(() => {
    if (themeMode === 'small' || themeMode === 'medium' || themeMode === 'large') return themeMode;
    if (viewportWidth >= 1536) return 'large';
    if (viewportWidth >= 1280) return 'medium';
    return 'small';
  }, [themeMode, viewportWidth]);

  // 主题配置
  const currentTokens = useMemo(() => {
    if (currentSizeKey === 'large') return tokensLarge;
    if (currentSizeKey === 'medium') return tokensMedium;
    return tokensSmall;
  }, [currentSizeKey]);

  const currentComponentSize = useMemo(() => {
    if (currentSizeKey === 'small') return 'small';
    return 'middle';
  }, [currentSizeKey]);

  const currentComponents = useMemo(() => {
    if (currentSizeKey === 'large') {
      return {
        Table: { cellPaddingBlock: 6, cellPaddingInline: 10 },
        Tabs: { titleFontSize: 12, horizontalItemPaddingLG: '14px 0' },
        Menu: { itemHeight: 40, itemPaddingInline: 16 },
        Form: { itemMarginBottom: 12 },
        Radio: { buttonPaddingInline: 15 },
      };
    }
    if (currentSizeKey === 'medium') {
      return {
        Table: { cellPaddingBlock: 5, cellPaddingInline: 8 },
        Tabs: { titleFontSize: 10, horizontalItemPaddingLG: '10px 0' },
        Menu: { itemHeight: 36, itemPaddingInline: 12 },
        Form: { itemMarginBottom: 8 },
        Radio: { buttonPaddingInline: 12 },
      };
    }
    return {
      Table: { cellPaddingBlock: 5, cellPaddingInline: 8 },
      Tabs: { titleFontSize: 10, horizontalItemPaddingLG: '10px 0' },
      Menu: { itemHeight: 32, itemPaddingInline: 12 },
      Form: { itemMarginBottom: 8 },
      Radio: { buttonPaddingInline: 15 },
    };
  }, [currentSizeKey]);

  const theme = useMemo(
    () => ({
      token: {
        colorPrimary: primaryColor,
        colorLink: primaryColor,
        ...currentTokens,
      },
      components: currentComponents
    }),
    [primaryColor, currentTokens, currentComponents]
  );

	return (
    <ConfigProvider locale={zhCN} theme={theme} componentSize={currentComponentSize}>
      <Router future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}>
        <RouterAuth>
            <AppRoutes />
        </RouterAuth>
      </Router>
    </ConfigProvider>
  );
}

export default Root;
